import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { getMerchandiseByEventId, createMerchandise, updateMerchandise, deleteMerchandise } from '../../../services/merchandiseService';

const MerchandiseTab = ({ event }) => {
  const [merchandise, setMerchandise] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (event?.id) {
      loadMerchandise();
    }
  }, [event?.id]);

  const loadMerchandise = async () => {
    try {
      setLoading(true);
      const data = await getMerchandiseByEventId(event?.id);
      setMerchandise(data || []);
    } catch (error) {
      setError(`Failed to load merchandise: ${error?.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateItem = () => {
    setEditingItem(null);
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this merchandise item?')) {
      return;
    }

    try {
      setError('');
      await deleteMerchandise(itemId);
      setSuccess('Merchandise item deleted successfully');
      loadMerchandise();
    } catch (error) {
      setError(`Failed to delete merchandise: ${error?.message}`);
    }
  };

  const handleSaveItem = async (itemData) => {
    try {
      setError('');
      setLoading(true);

      if (editingItem?.id) {
        await updateMerchandise(editingItem?.id, itemData);
        setSuccess('Merchandise updated successfully');
      } else {
        await createMerchandise({ ...itemData, event_id: event?.id });
        setSuccess('Merchandise created successfully');
      }

      setShowForm(false);
      setEditingItem(null);
      loadMerchandise();
    } catch (error) {
      setError(`Failed to save merchandise: ${error?.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!event?.id) {
    return (
      <div className="p-6 text-center">
        <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
          <span className="text-2xl text-muted-foreground">🛍️</span>
        </div>
        <h3 className="text-lg font-medium text-foreground mb-2">No Event Selected</h3>
        <p className="text-sm text-muted-foreground">
          Please create or select an event first to manage merchandise
        </p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Merchandise</h2>
          <p className="text-sm text-muted-foreground">
            Manage merchandise items for {event?.title}
          </p>
        </div>
        <Button
          variant="default"
          onClick={handleCreateItem}
          iconName="Plus"
        >
          Add Merchandise
        </Button>
      </div>
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}
      {/* Merchandise List */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)]?.map((_, index) => (
            <div key={index} className="animate-pulse">
              <div className="bg-muted rounded-lg p-4">
                <div className="h-4 bg-muted-foreground/20 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-muted-foreground/20 rounded w-1/2 mb-2"></div>
                <div className="h-3 bg-muted-foreground/20 rounded w-2/3"></div>
              </div>
            </div>
          ))}
        </div>
      ) : merchandise?.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl text-muted-foreground">🛍️</span>
          </div>
          <h3 className="text-lg font-medium text-foreground mb-2">No Merchandise Yet</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Start selling event merchandise by adding your first item
          </p>
          <Button variant="default" onClick={handleCreateItem}>
            Add First Item
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {merchandise?.map(item => (
            <div key={item?.id} className="bg-card border border-border rounded-lg overflow-hidden">
              {item?.image_url && (
                <div className="aspect-video bg-muted">
                  <img 
                    src={item?.image_url} 
                    alt={item?.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              
              <div className="p-4">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="font-medium text-foreground">{item?.name}</h3>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditItem(item)}
                      iconName="Edit"
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteItem(item?.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      Delete
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Price:</span>
                    <span className="font-medium">₹{item?.price}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Stock:</span>
                    <span>{item?.stock_quantity}</span>
                  </div>
                  {item?.sku && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">SKU:</span>
                      <span className="text-xs font-mono">{item?.sku}</span>
                    </div>
                  )}
                </div>

                {item?.description && (
                  <p className="text-sm text-muted-foreground mt-3 line-clamp-2">
                    {item?.description}
                  </p>
                )}

                <div className={`mt-3 px-2 py-1 rounded-full text-xs inline-block ${
                  item?.status === 'active' ?'bg-green-100 text-green-800'
                    : item?.status === 'out_of_stock' ?'bg-red-100 text-red-800' :'bg-gray-100 text-gray-800'
                }`}>
                  {item?.status === 'active' ? 'Available' : 
                   item?.status === 'out_of_stock' ? 'Out of Stock' : 'Inactive'}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Merchandise Form Modal */}
      {showForm && (
        <MerchandiseForm
          item={editingItem}
          onSave={handleSaveItem}
          onCancel={() => {
            setShowForm(false);
            setEditingItem(null);
          }}
          loading={loading}
        />
      )}
    </div>
  );
};

const MerchandiseForm = ({ item, onSave, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    sku: '',
    stock_quantity: '',
    image_url: '',
    status: 'active'
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (item) {
      setFormData({
        name: item?.name || '',
        description: item?.description || '',
        price: item?.price?.toString() || '',
        sku: item?.sku || '',
        stock_quantity: item?.stock_quantity?.toString() || '',
        image_url: item?.image_url || '',
        status: item?.status || 'active'
      });
    }
  }, [item]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Item name is required';
    }

    if (!formData?.price || isNaN(parseFloat(formData?.price))) {
      newErrors.price = 'Valid price is required';
    }

    if (!formData?.stock_quantity || isNaN(parseInt(formData?.stock_quantity))) {
      newErrors.stock_quantity = 'Valid stock quantity is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    const submitData = {
      ...formData,
      price: parseFloat(formData?.price),
      stock_quantity: parseInt(formData?.stock_quantity)
    };

    await onSave?.(submitData);
  };

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'out_of_stock', label: 'Out of Stock' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-card border border-border rounded-lg shadow-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-foreground">
              {item ? 'Edit Merchandise' : 'Add Merchandise'}
            </h3>
            <Button variant="outline" onClick={onCancel}>
              ×
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <Input
                label="Item Name"
                placeholder="e.g., Event T-Shirt"
                value={formData?.name}
                onChange={(e) => handleInputChange('name', e?.target?.value)}
                error={errors?.name}
                required
              />

              <Input
                label="Description"
                placeholder="Describe this merchandise item"
                value={formData?.description}
                onChange={(e) => handleInputChange('description', e?.target?.value)}
                multiline
                rows={3}
              />

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Price (₹)"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={formData?.price}
                  onChange={(e) => handleInputChange('price', e?.target?.value)}
                  error={errors?.price}
                  required
                />

                <Input
                  label="Stock Quantity"
                  type="number"
                  placeholder="100"
                  value={formData?.stock_quantity}
                  onChange={(e) => handleInputChange('stock_quantity', e?.target?.value)}
                  error={errors?.stock_quantity}
                  required
                />
              </div>
            </div>

            <div className="space-y-4">
              <Input
                label="SKU (Stock Keeping Unit)"
                placeholder="e.g., TSHIRT-001"
                value={formData?.sku}
                onChange={(e) => handleInputChange('sku', e?.target?.value)}
              />

              <Input
                label="Image URL"
                placeholder="https://example.com/image.jpg"
                value={formData?.image_url}
                onChange={(e) => handleInputChange('image_url', e?.target?.value)}
              />

              <Select
                label="Status"
                value={formData?.status}
                onChange={(e) => handleInputChange('status', e?.target?.value)}
                options={statusOptions}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-border">
            <Button variant="outline" onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
            <Button variant="default" onClick={handleSubmit} loading={loading}>
              {item ? 'Update Item' : 'Add Item'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MerchandiseTab;